/**
 * UI-8e Task 8 — FeatureFlagsPage.
 *
 * Platform-wide feature-flag admin surface. Composition:
 *
 *   - `useFeatureFlags()` drives the grouped list + mutation helpers.
 *   - PageHeader title "Feature Flags" + "+ New Flag" CTA.
 *   - Loading            → PageSkeleton.
 *   - Empty              → EmptyState with description + secondary CTA.
 *   - Otherwise          → one FlagAccordionItem per grouped flag.
 *   - NewFlagDrawer is mounted at the page level so the "+ New Flag" CTA
 *     can open it from either the header or the empty-state action slot.
 *
 * Permission: `platform:feature_flags_admin` (PLATFORM_FEATURE_FLAGS_ADMIN).
 * The page wraps itself in <RequirePermission>; the router does not need
 * additional gating.
 *
 * Sibling reference: KeyRotationPage (UI-8c Task 8) — same shape with
 * RequirePermission + PageHeader + hook-driven body.
 */
import { useState, type JSX } from 'react'
import { Plus, Flag } from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import { FlagAccordionItem } from '../../components/platform/FlagAccordionItem.js'
import { NewFlagDrawer } from '../../components/platform/NewFlagDrawer.js'
import { useFeatureFlags } from '../../hooks/useFeatureFlags.js'

function StatCard({ label, value, icon: Icon, accent }: {
  label: string
  value: number
  icon: typeof Flag
  accent: string
}) {
  return (
    <div className="relative bg-white rounded-sm border border-gray-100 p-5 overflow-hidden group hover:shadow-md hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${accent}10` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: accent }} />
    </div>
  )
}

export default function FeatureFlagsPage(): JSX.Element {
  const { flags, loading, refetch } = useFeatureFlags()
  const [drawerOpen, setDrawerOpen] = useState(false)

  function openDrawer(): void { setDrawerOpen(true) }
  function closeDrawer(): void {
    setDrawerOpen(false)
    void refetch()
  }

  const globalCount = flags.filter(f => f.global).length
  const overrideCount = flags.reduce((acc, f) => acc + f.overrides.length, 0)

  return (
    <RequirePermission permission="platform:feature_flags_admin">
      <div className="w-full space-y-5 px-6 pb-10">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
              <Flag className="h-5 w-5 text-[#3454cf]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Feature Flags</h1>
              <p className="text-sm text-gray-400 mt-0.5">Toggle features per tenant with global defaults and overrides.</p>
            </div>
          </div>
          <button
            onClick={openDrawer}
            className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md"
          >
            <Plus className="h-4 w-4" />
            New Flag
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard label="Total Flags" value={flags.length} icon={Flag} accent="#3454cf" />
          <StatCard label="Global Configs" value={globalCount} icon={Flag} accent="#10b981" />
          <StatCard label="Overrides" value={overrideCount} icon={Flag} accent="#f6931c" />
          <StatCard label="Active Flags" value={flags.filter(f => f.global?.enabled).length} icon={Flag} accent="#8b5cf6" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="pi pi-spin pi-spinner text-[#3454cf] text-2xl" />
          </div>
        ) : flags.length === 0 ? (
          <div className="text-center py-20 text-gray-300">
            <Flag className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium text-gray-400">No feature flags yet</p>
            <p className="text-xs text-gray-300 mt-1">Create your first flag to roll out a new capability gradually.</p>
            <button
              onClick={openDrawer}
              className="mt-4 inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md"
            >
              <Plus className="h-4 w-4" />
              New Flag
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {flags.map((flag) => (
              <FlagAccordionItem key={flag.flagKey} flag={flag} onChanged={refetch} />
            ))}
          </div>
        )}

        <NewFlagDrawer open={drawerOpen} onClose={closeDrawer} />
      </div>
    </RequirePermission>
  )
}
